import { request } from './client'

/* The roster — who is working, and when.

   A GRID: people down, days across. The server sends the people separately from the
   cells, and that is not a normalisation for its own sake: `openTaskCount` is a fact
   about a PERSON, not about a day, because a task in this product is not dated. Putting
   it on a cell would print the same figure seven times across a row and imply, falsely,
   that we know what somebody is holding next Tuesday. */

/* RosterState is NOT redeclared here. It is the same four words the assignment board
   already names in ./work, and a second declaration — even an identical one — is how
   two lists of the same enum end up one member apart. Imported and re-exported so the
   roster's own consumers do not have to know which file it came from. */
export type { RosterState } from './work'
import type { RosterState } from './work'

export interface RosterPerson {
  userId: string
  employeeId: string
  displayName: string
  displayNameAr: string | null
  roleCode: string
  /** Open tasks assigned to them right now — what has to go somewhere else if they
   *  are marked absent today. */
  openTaskCount: number
}

export interface RosterCell {
  userId: string
  employeeId: string
  displayName: string
  displayNameAr: string | null
  roleCode: string
  /** The property's LOCAL date, midnight. Never a UTC instant. */
  localDate: string
  /**
   * NULL MEANS NOBODY HAS SAID YET, and it is a different fact from a rest day.
   * If the two looked the same, a roster nobody had written would be indistinguishable
   * from one where everybody is off.
   */
  state: RosterState | null
  shiftId: string | null
  shiftName: string | null
  shiftNameAr: string | null
  coverage: string | null
  /** Derived by the DOMAIN rule and sent, so this screen and the assignment board cannot
   *  disagree about who may be given a room. */
  mayReceiveWork: boolean
}

export interface ShiftOption {
  shiftId: string
  code: string
  name: string
  nameAr: string | null
  startMinuteLocal: number
  endMinuteLocal: number
}

export interface RosterWeek {
  propertyId: string
  dates: string[]
  /**
   * THE HOTEL'S TODAY, so the grid can mark the current column without asking the
   * browser what day it is. A supervisor in Najaf on a laptop still set to London
   * would otherwise see the wrong column highlighted for three hours every evening —
   * and would be marking absences against it.
   */
  today: string
  people: RosterPerson[]
  cells: RosterCell[]
  shifts: ShiftOption[]
}

/** Cells keyed `userId|localDate`, so a grid can look one up without scanning. */
export function cellIndex(week: RosterWeek): Map<string, RosterCell> {
  return new Map(week.cells.map((c) => [cellKey(c.userId, c.localDate), c]))
}

export function cellKey(userId: string, localDate: string): string {
  // The server sends midnight local; the wire form may carry a time. Only the date
  // identifies a cell, and comparing the whole string would miss every match.
  return `${userId}|${localDate.slice(0, 10)}`
}

export function fetchRoster(
  propertyId: string,
  options: { from?: string; signal?: AbortSignal } = {},
): Promise<RosterWeek> {
  const query = options.from ? `?from=${options.from}` : ''
  return request<RosterWeek>(
    `/properties/${propertyId}/roster${query}`,
    options.signal ? { signal: options.signal } : {})
}

export function setRosterEntry(
  propertyId: string,
  body: {
    userId: string
    localDate: string
    state: RosterState
    shiftId?: string | null
    coverage?: string | null
  },
): Promise<RosterCell> {
  return request<RosterCell>(`/properties/${propertyId}/roster`, { method: 'PUT', body })
}

/** The local date as the wire wants it: `YYYY-MM-DD`, with no time and no zone.
 *  `toISOString()` would convert to UTC and move the date by a day for half the world. */
export function localDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * The inverse of `localDateKey`, and it must not be `new Date(key)`.
 *
 * `new Date('2026-09-05')` is parsed as UTC MIDNIGHT by specification, so anywhere west
 * of Greenwich it renders as the fourth of September — the roster's column headers would
 * be a day out for half the world, silently, and only for date-only strings. Split into
 * parts and built with the local constructor, it is the day it says it is.
 */
export function localDateFromKey(key: string): Date {
  const [y, m, d] = key.slice(0, 10).split('-').map(Number)
  return new Date(y!, m! - 1, d!)
}

/** Add days to a `YYYY-MM-DD` key, staying in local time. */
export function shiftDateKey(key: string, days: number): string {
  const d = localDateFromKey(key)
  d.setDate(d.getDate() + days)
  return localDateKey(d)
}
